import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2, SearchX, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { ProductCard } from '../ProductCard/ProductCard';
import { useProducts } from '../../services/productService';
import { useFilterStore } from '../../stores/filterStore';
import { useDebounce } from '../../hooks/useDebounce';

/**
 * Product list with numbered pagination — vertical list
 * of cards that works seamlessly on all screen sizes.
 */
export function ProductGrid() {
  const { search, categoryId, sortBy, minPrice, maxPrice } = useFilterStore();
  const debouncedSearch = useDebounce(search, 300);
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, categoryId, sortBy, minPrice, maxPrice]);

  const filters = {
    search: debouncedSearch,
    categoryId,
    sortBy,
    minPrice,
    maxPrice,
    page: currentPage,
  };

  const { data, isLoading, error } = useProducts(filters);

  const allProducts = data?.data ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Error
  if (error) {
    return (
      <div className="py-16 text-center">
        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-error/10 flex items-center justify-center">
          <Package size={24} className="text-error" />
        </div>
        <p className="text-error text-sm font-semibold">Failed to load products</p>
        <p className="text-xs text-brand-gray mt-1">Please check your connection and try again.</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Results header */}
      {!isLoading && (
        <div className="mb-4 pb-3 border-b border-ui-border flex items-center justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-brand-dark">
              All Results
            </h2>
            <p className="text-xs text-brand-gray mt-0.5">
              {totalCount} {totalCount === 1 ? 'product' : 'products'} found
            </p>
          </div>
          {/* Realtime indicator */}
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-[10px] sm:text-xs text-brand-gray font-medium">Live</span>
          </div>
        </div>
      )}

      {/* Loading skeletons */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, i) => (
            <div
              key={i}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-3 sm:p-4 bg-brand-white rounded-xl border border-ui-border animate-pulse"
            >
              <div className="w-full sm:w-48 lg:w-56 h-40 sm:h-36 bg-brand-light rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-3 py-1">
                <div className="h-5 bg-brand-light rounded w-2/3" />
                <div className="h-3 bg-brand-light rounded w-24" />
                <div className="h-3 bg-brand-light rounded w-full" />
                <div className="h-3 bg-brand-light rounded w-3/4" />
                <div className="flex items-center justify-between mt-2">
                  <div className="h-5 bg-brand-light rounded w-20" />
                  <div className="h-8 bg-brand-light rounded w-32" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product list */}
      {!isLoading && allProducts.length > 0 && (
        <motion.div layout className="space-y-3">
          {allProducts.map((product, i) => (
            <ProductCard
              key={product.id}
              product={product}
              index={i + 1}
            />
          ))}
        </motion.div>
      )}

      {/* Empty state */}
      {!isLoading && allProducts.length === 0 && (
        <div className="py-16 text-center">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-brand-light flex items-center justify-center">
            <SearchX size={24} className="text-ui-muted" />
          </div>
          <h3 className="text-base font-bold text-brand-dark">No products found</h3>
          <p className="text-sm text-brand-gray mt-1">
            Try adjusting your filters or search terms
          </p>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && allProducts.length > 0 && totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          {/* Previous button */}
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={clsx(
              'flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              currentPage === 1
                ? 'bg-brand-light text-ui-muted cursor-not-allowed'
                : 'bg-brand-white border border-ui-border text-brand-dark hover:bg-brand-light'
            )}
          >
            <ChevronLeft size={16} />
            <span className="hidden sm:inline">Previous</span>
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              // Show first, last, current, and adjacent pages
              const showPage =
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1);

              // Show ellipsis
              const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
              const showEllipsisAfter = page === currentPage + 2 && currentPage < totalPages - 2;

              if (!showPage && !showEllipsisBefore && !showEllipsisAfter) return null;

              if (showEllipsisBefore || showEllipsisAfter) {
                return (
                  <span key={page} className="px-2 text-brand-gray">
                    ...
                  </span>
                );
              }

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={clsx(
                    'w-9 h-9 rounded-lg text-sm font-semibold transition-colors',
                    page === currentPage
                      ? 'bg-brand-red text-brand-white'
                      : 'bg-brand-white border border-ui-border text-brand-dark hover:bg-brand-light'
                  )}
                >
                  {page}
                </button>
              );
            })}
          </div>

          {/* Next button */}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={clsx(
              'flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              currentPage === totalPages
                ? 'bg-brand-light text-ui-muted cursor-not-allowed'
                : 'bg-brand-white border border-ui-border text-brand-dark hover:bg-brand-light'
            )}
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
